"use client";

import { useSearchParams } from "next/navigation";
import { Search, ChevronRight, X } from "lucide-react";
import Image from "next/image";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import clsx from "clsx";
import useLocaleLegacy from "@/hooks/use-locale-legacy";
import locale from "@/locales/header.json";
import { useEffect, useMemo, useState } from "react";
import SidebarMobile from "@/components/sidebar-mobile";
import isExternal from "@/lib/is-external";
import { useLocale } from "next-intl";
import {
  getPathname,
  Link,
  usePathname,
  useRouter,
} from "@/src/i18n/navigation";

interface Menu {
  id: string;
  text: string;
  link: string;
  content_id: string;
  children: Menu[];
}

export interface HeaderProps {
  menu: Menu[];
}

export default function Header(props: HeaderProps) {
  const { t } = useLocaleLegacy(locale);

  const [showSearch, setShowSearch] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);

  const pathname = usePathname();
  const qs = useSearchParams();
  const lang = useLocale();
  const router = useRouter();

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isMobile = window.innerWidth < 1200;
      const hasScrolled = window.scrollY > 0;

      setIsScrolled(isMobile && hasScrolled);
    };

    // Attach listener
    window.addEventListener("scroll", handleScroll);
    // Also check on resize, because width might change
    window.addEventListener("resize", handleScroll);

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);

  return (
    <>
      <SidebarMobile
        show={showSidebar}
        onClose={() => setShowSidebar(false)}
        menu={props.menu}
      />
      <header
        id="navbar"
        className={clsx(
          "flex w-screen fixed top-0 left-0 z-50 bg-transparent xl:bg-white transition-all duration-500",
          {
            "invisible opacity-0": pathname === "/",
          }
        )}
      >
        <div className="w-full xl:max-w-screen-xl mx-auto flex items-center">
          <Link
            locale={lang}
            href={`/?${new URLSearchParams(qs).toString()}`}
            className="hidden xl:block"
          >
            <Image
              src="/elements-logo.svg"
              alt="logo"
              width={215}
              height={80}
              className="py-4 h-20 object-cover"
            />
          </Link>

          {!showSearch && (
            <div className="hidden xl:flex items-center justify-between w-full ml-16 self-stretch h-auto">
              {props.menu.map((menu) => {
                const isActivePath =
                  menu.link === "/"
                    ? menu.link === pathname
                    : pathname?.startsWith(menu.link);

                if (menu.children.length > 0) {
                  return (
                    <DropdownMenu key={menu.id}>
                      <DropdownMenuTrigger asChild>
                        <div
                          className={clsx("text-brown cursor-pointer text-xl", {
                            "font-semibold text-gold": isActivePath,
                          })}
                          dangerouslySetInnerHTML={{ __html: menu.text }}
                        ></div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="rounded-none bg-gold p-0 border-none">
                        {menu.children.map((child, idx, arr) => {
                          const isActivePath = child.link === pathname;

                          const link = `${child.link}?${new URLSearchParams(
                            qs
                          ).toString()}`;
                          return (
                            <Link locale={lang} key={child.id} href={link}>
                              <DropdownMenuItem
                                className={clsx(
                                  "py-3 mx-3 rounded-none flex justify-center font-medium text-coffebrown",
                                  {
                                    "border-b border-b-white":
                                      idx < arr.length - 1,
                                    "text-white": isActivePath,
                                  }
                                )}
                              >
                                {child.text}
                              </DropdownMenuItem>
                            </Link>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }
                const link = `${menu.link}?${new URLSearchParams(
                  qs
                ).toString()}`;

                return (
                  <Link
                    locale={lang}
                    prefetch={!isExternal(link)}
                    key={menu.id}
                    href={link}
                    target={isExternal(link) ? "_blank" : "_self"}
                    className={clsx("h-full flex items-center relative", {
                      // "border-b-[6] border-b-gold": isActivePath,
                    })}
                  >
                    <div
                      className={clsx("text-brown text-xl text-center", {
                        "font-semibold text-gold": isActivePath,
                      })}
                      dangerouslySetInnerHTML={{ __html: menu.text }}
                    ></div>

                    {isActivePath && (
                      <div className="h-1.5 bg-gold absolute bottom-0 w-full"></div>
                    )}
                  </Link>
                );
              })}

              <div className="inline-flex items-center">
                <button
                  onClick={() => {
                    setShowSearch(true);
                  }}
                >
                  <Search
                    size={21}
                    className="text-gold hover:cursor-pointer"
                  />
                </button>
                <Link href={`/inquiry?${new URLSearchParams(qs).toString()}`}>
                  <button className="rounded-[4rem] border border-gold text-gold font-semibold py-2 px-6 mx-4 cursor-pointer hover:!bg-linen hover:!text-brown text-xl">
                    {t("inquiry")}
                  </button>
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <div className="inline-flex items-center justify-around text-gold font-medium w-16 cursor-pointer">
                      {lang === "id-ID" ? "IND" : "ENG"}
                      <ChevronRight size={18} />
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="rounded-none bg-gold p-0 border-none w-16"
                  >
                    <DropdownMenuItem asChild>
                      <Link
                        prefetch
                        locale={lang === "id-ID" ? "en" : "id-ID"}
                        href={pathname}
                        className="inline-flex space-x-3 bg-gold px-3 text-white py-3 rounded-none font-medium w-full"
                      >
                        {lang === "id-ID" ? "ENG" : "ID"}
                        <ChevronRight color="white" size={18} />
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}

          {
            //mobile version
            !showSearch && (
              <div
                className={clsx(
                  "flex items-center xl:hidden py-1 px-7 w-full justify-between transition-all duration-500",
                  {
                    "!bg-white": isScrolled,
                  }
                )}
              >
                <div className="flex-1">
                  <button
                    onClick={() => {
                      setShowSidebar(true);
                    }}
                    className="w-6 flex flex-col gap-y-2 cursor-pointer"
                  >
                    <div className="h-[1px] bg-gold w-full"></div>
                    <div className="h-[1px] bg-gold w-full"></div>
                  </button>
                </div>
                <Link href={"/"}>
                  <img
                    src="/elements-logo-mobile-cokelat.png"
                    alt="logo"
                    width={113}
                    height={45}
                    className={clsx("w-[113px] h-[45px] object-contain hidden", {
                      "!block": isScrolled,
                    })}
                  />
                  <img
                    src={"/elements-logo-mobile.png"}
                    alt="logo"
                    width={113}
                    height={45}
                    className={clsx("w-[113px] h-[45px] object-contain block", {
                      "!hidden": isScrolled,
                    })}
                  />
                </Link>

                <div className="flex-1 flex justify-end">
                  <Link prefetch href={"/inquiry"}>
                    <button className="rounded-[69px] border border-gold text-[11px] text-gold px-3 py-0.5 font-karbon font-semibold">
                      Inquiry
                    </button>
                  </Link>
                </div>
              </div>
            )
          }

          {showSearch && (
            <div className="rounded-[1.875rem]  border border-gold flex items-center w-[555px] py-2 px-4 mx-auto">
              <Search className="text-gold" />

              <input
                type="text"
                placeholder="Search"
                className="text-gold font-karbon text-xl ml-3 flex-1 focus:outline-none font-medium"
                defaultValue={qs?.get("q") || ""}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const searchParam = new URLSearchParams(qs);
                    if (e.currentTarget.value) {
                      searchParam.set("q", e.currentTarget.value);
                    } else {
                      searchParam.delete("q");
                    }

                    if (pathname === "/search") {
                      router.push(`/search?${searchParam.toString()}`, {
                        locale: lang,
                      });
                    } else {
                      setShowSearch(false);
                      const pathname = getPathname({
                        href: {
                          pathname: "/search",
                          query: Object.fromEntries(searchParam.entries()),
                        },
                        locale: lang,
                      });
                      window.open(pathname, "_blank");
                    }
                  }
                }}
              />

              <button
                className="border-[2] border-gold rounded-full cursor-pointer"
                onClick={() => {
                  setShowSearch(false);
                }}
              >
                <X className="text-gold" size={16} />
              </button>
            </div>
          )}
        </div>
      </header>
    </>
  );
}
